import React, { useState, useEffect, useRef, useMemo } from "react";
import { useParams } from "react-router-dom";
import { API_URL } from "@/env";
import { showCustomToast } from "@/components/CustomToast";
import { toast } from "react-hot-toast";
import ToolbarLogo from "@/assets/icons/ToolbarLogo.svg";
import Screen from "@/assets/icons/Screen.svg";
import ScreenVoice from "@/assets/icons/ScreenVoice.svg";
import ScreenVoiceSelected from "@/assets/icons/ScreenVoiceSelected.svg";
import RecordingIcon from "@/assets/icons/RecordingIcon.svg";
import Checkmark from "@/assets/icons/Checkmark.svg";
import Microphone from "@/assets/icons/Microphone.svg";
import { AnimatePresence, motion } from "motion/react";
import ChevronDownSmWhite from "@/assets/icons/ChevronDownSmWhite.svg";
import MuteSm from "@/assets/icons/MuteSm.svg";
import Volume from "@/assets/icons/Volume.svg";
import SendPaper from "@/assets/icons/SendPaper.svg";
import CatLike from "@/assets/CatLike.png";
import { getDeviceInfo } from "@/utils/getDeviceInfo";
import { getCountry } from "@/utils/getCountry";
import { useOutsideClick } from "@/hooks/useOutsideClick";
import {
  initializeUpload,
  addChunk,
  finalizeUpload,
  abortUpload,
  type UploadSession,
} from "@/utils/progressiveUpload";

const btns = [
  {
    iconSelected: ScreenVoiceSelected,
    icon: ScreenVoice,
    title: "Screen + voice",
    withMicrophone: true,
  },
  {
    iconSelected: Screen,
    icon: Screen,
    title: "Screen only",
    withMicrophone: false,
  },
];

const RecordLink = () => {
  const { token } = useParams();
  const [isValidating, setIsValidating] = useState(true);
  const [isValid, setIsValid] = useState(false);
  const [linkData, setLinkData] = useState<any>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(
    null
  );
  const [isUploading, setIsUploading] = useState(false);
  const [selectedType, setSelectedType] = useState<number>(0);
  const chunksRef = useRef<Blob[]>([]);
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
  const [s3VideoUrl, setS3VideoUrl] = useState<string | null>(null);
  const uploadSessionRef = useRef<UploadSession | null>(null);
  const s3FinalizePromiseRef = useRef<Promise<any> | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [audioDevices, setAudioDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedMicId, setSelectedMicId] = useState<string>("");
  const [showMicSelection, setShowMicSelection] = useState(false);
  const [isMicDropdownOpen, setIsMicDropdownOpen] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const videoPreviewRef = useRef<HTMLVideoElement>(null);
  const [previewStream, setPreviewStream] = useState<MediaStream | null>(null);
  const isCancellingRef = useRef(false);
  const [isMuted, setIsMuted] = useState(true);
  const playbackVideoRef = useRef<HTMLVideoElement>(null);
  const [recordingMetadata, setRecordingMetadata] = useState<{
    timestamp: string;
    os: string;
    browser: string;
    browserVersion: string;
    windowWidth: number;
    windowHeight: number;
    country: string;
  } | null>(null);
  const wrongSelectionToastIdRef = useRef<string | null>(null);
  const micDropdownRef = useRef<HTMLDivElement>(null);

  useOutsideClick(micDropdownRef, () => {
    if (isMicDropdownOpen) {
      setIsMicDropdownOpen(false);
    }
  });

  useEffect(() => {
    validateLink();
  }, [token]);

  useEffect(() => {
  }, [recordingMetadata]);

  useEffect(() => {
    // Show microphone selection if "Screen + voice" is selected initially
    if (btns[selectedType].withMicrophone && isValid) {
      getAudioDevices();
      setShowMicSelection(true);
    }
  }, [isValid]);

  const formatTime = (seconds: number) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, "0")}:${secs
        .toString()
        .padStart(2, "0")}`;
    }
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const validateLink = async () => {
    try {
      setIsValidating(true);
      const response = await fetch(`${API_URL}/recording-links/${token}`, {
        method: "GET",
      });

      const result = await response.json();

      if (result.valid) {
        setIsValid(true);
        setLinkData(result.link);
      } else {
        setIsValid(false);
        // showCustomToast(result.message || "Invalid link", "exclamation");
      }
    } catch (error) {
      setIsValid(false);
      // showCustomToast("Failed to validate link", "exclamation");
    } finally {
      setIsValidating(false);
    }
  };

  const getAudioDevices = async () => {
    try {
      // Request microphone permission first
      await navigator.mediaDevices.getUserMedia({ audio: true });

      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioInputs = devices.filter(
        (device) => device.kind === "audioinput"
      );
      setAudioDevices(audioInputs);

      // Set default microphone if available
      if (audioInputs.length > 0 && !selectedMicId) {
        setSelectedMicId(audioInputs[0].deviceId);
      }
    } catch (error) {
      showCustomToast("Could not access microphone", "exclamation");
    }
  };

  const startRecording = async (withMicrophone: boolean) => {
    try {
      // Dismiss the wrong selection toast if it exists
      if (wrongSelectionToastIdRef.current) {
        toast.dismiss(wrongSelectionToastIdRef.current);
        wrongSelectionToastIdRef.current = null;
      }

      // Reset chunks and state
      chunksRef.current = [];
      setS3VideoUrl(null);
      s3FinalizePromiseRef.current = null;

      // Capture device info and metadata
      const deviceInfo = getDeviceInfo();
      const country = await getCountry();
      const metadata = {
        timestamp: new Date().toISOString(),
        os: deviceInfo.os,
        browser: deviceInfo.browser,
        browserVersion: deviceInfo.browserVersion,
        windowWidth: deviceInfo.screenWidth,
        windowHeight: deviceInfo.screenHeight,
        country: country,
      };
      setRecordingMetadata(metadata);

      const displayMediaOptions: DisplayMediaStreamOptions = {
        video: {
          width: { ideal: 1920, max: 1920 },
          height: { ideal: 1080, max: 1080 },
          frameRate: { ideal: 30, max: 30 },
        },
        audio: false,
      };

      // Try to default to screen instead of tab when redirectLink exists
      if (linkData?.redirectLink) {
        (displayMediaOptions as any).preferCurrentTab = false;
        (displayMediaOptions as any).surfaceSwitching = "exclude";
        (displayMediaOptions as any).selfBrowserSurface = "exclude";
        (displayMediaOptions.video as any).displaySurface = "monitor";
      }

      const displayStream = await navigator.mediaDevices.getDisplayMedia(displayMediaOptions);

      // If there's a redirectLink, verify user selected entire screen (not a tab/window)
      if (linkData?.redirectLink) {
        const videoTrack = displayStream.getVideoTracks()[0];
        const settings = videoTrack.getSettings();

        // Check if user selected a browser tab or window instead of entire screen
        // displaySurface can be: "monitor" (entire screen), "window", or "browser" (tab)
        if (settings.displaySurface === "browser" || settings.displaySurface === "window") {
          // Stop the stream
          displayStream.getTracks().forEach(track => track.stop());

          // Show persistent toast that won't auto-dismiss
          const toastId = showCustomToast(
            "Please select 'Entire Screen' instead of a tab or window",
            "exclamation",
            true
          );
          wrongSelectionToastIdRef.current = toastId;

          return;
        }
      }

      // Initialize progressive uploader AFTER screen capture is granted
      const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
      const filename = `recording-${timestamp}.webm`;

      const uploadSession = await initializeUpload(filename);
      uploadSessionRef.current = uploadSession;

      // Open the redirectLink ONLY if user selected entire screen (redirectLink exists and wasn't blocked above)
      if (linkData?.redirectLink) {
        // Ensure the URL has a protocol (https://) to avoid relative URL issues
        const url =
          linkData.redirectLink.startsWith("http://") ||
          linkData.redirectLink.startsWith("https://")
            ? linkData.redirectLink
            : `https://${linkData.redirectLink}`;
        window.open(url, "_blank", "noopener,noreferrer");
      }

      let audioStream: MediaStream | null = null;
      if (withMicrophone) {
        try {
          audioStream = await navigator.mediaDevices.getUserMedia({
            audio: selectedMicId
              ? { deviceId: { exact: selectedMicId } }
              : true,
          });
        } catch (error) {
          showCustomToast("Could not access microphone", "exclamation");
        }
      }

      const tracks = [
        ...displayStream.getVideoTracks(),
        ...(audioStream ? audioStream.getAudioTracks() : []),
      ];
      const combinedStream = new MediaStream(tracks);

      // Test all supported MIME types and choose the best one
      const types = [
        "video/webm;codecs=vp9,opus",
        "video/webm;codecs=vp8,opus",
        "video/webm;codecs=h264,opus",
        "video/webm;codecs=vp9",
        "video/webm;codecs=vp8",
        "video/webm;codecs=h264",
        "video/webm",
        "video/mp4",
      ];

      let mimeType = "";
      for (const type of types) {
        if (MediaRecorder.isTypeSupported(type)) {
          mimeType = type;
          break;
        }
      }

      // Get video track settings to determine what's being captured
      const videoSettings = combinedStream.getVideoTracks()[0]?.getSettings();
      const isHighRes = videoSettings && (videoSettings.width || 0) > 1920;

      const recorderOptions: MediaRecorderOptions = {
        videoBitsPerSecond: isHighRes ? 5000000 : 2500000, // 5 Mbps for high-res, 2.5 Mbps otherwise
      };

      // Only set mimeType if we have one
      if (mimeType) {
        recorderOptions.mimeType = mimeType;
      }

      const recorder = new MediaRecorder(combinedStream, recorderOptions);

      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) {
          // Store chunk for local playback
          chunksRef.current.push(e.data);

          // Upload chunk to S3 in real-time (non-blocking)
          if (uploadSessionRef.current) {
            addChunk(uploadSessionRef.current, e.data).catch((error) => {
              console.error("Failed to upload chunk:", error);
            });
          }
        }
      };

      recorder.onstop = async () => {
        // Stop timer
        if (timerIntervalRef.current) {
          clearInterval(timerIntervalRef.current);
          timerIntervalRef.current = null;
        }

        // If cancelling, abort the upload and don't save
        if (isCancellingRef.current) {
          if (uploadSessionRef.current) {
            await abortUpload(uploadSessionRef.current);
            uploadSessionRef.current = null;
          }
          setIsRecording(false);
          isCancellingRef.current = false;
          return;
        }

        const totalSize = chunksRef.current.reduce(
          (acc, chunk) => acc + chunk.size,
          0
        );

        if (chunksRef.current.length === 0 || totalSize === 0) {
          showCustomToast(
            "Recording failed - no data captured. This may be a browser compatibility issue.",
            "exclamation"
          );
          setIsRecording(false);
          if (uploadSessionRef.current) {
            await abortUpload(uploadSessionRef.current);
            uploadSessionRef.current = null;
          }
          return;
        }

        // Create local blob for IMMEDIATE preview
        const blobType = chunksRef.current[0]?.type || mimeType || "video/webm";
        const blob = new Blob(chunksRef.current, { type: blobType });

        setIsRecording(false);
        setRecordedBlob(blob);

        // Finalize S3 upload in the BACKGROUND (non-blocking)
        if (uploadSessionRef.current) {
          const finalizePromise = finalizeUpload(uploadSessionRef.current)
            .then((result) => {
              setS3VideoUrl(result.url);
              return result;
            })
            .catch((error) => {
              console.error("❌ Failed to finalize S3 upload:", error);
              setS3VideoUrl(null);
              throw error;
            });

          s3FinalizePromiseRef.current = finalizePromise;
        }
      };

      recorder.onerror = () => {
        showCustomToast("Recording error occurred", "exclamation");
      };

      // Stop when user clicks "Stop Sharing"
      displayStream.getVideoTracks()[0].onended = () => {
        if (recorder.state !== "inactive") {
          recorder.stop();
        }
        combinedStream.getTracks().forEach((track) => track.stop());
      };

      // Start recording with 100ms chunks
      recorder.start(100);

      setMediaRecorder(recorder);
      setIsRecording(true);
      setRecordingTime(0);

      // Set video preview - use the display stream directly for preview
      setPreviewStream(displayStream);

      // Start timer
      timerIntervalRef.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    } catch (error) {
      showCustomToast("Failed to start recording", "exclamation");
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && mediaRecorder.state !== "inactive") {
      mediaRecorder.stop();
      if (mediaRecorder.stream) {
        mediaRecorder.stream.getTracks().forEach((track) => track.stop());
      }
    }

    // Clear video preview
    setPreviewStream(null);

    // Stop timer
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
  };

  const handleTryAgain = () => {
    setRecordedBlob(null);
    chunksRef.current = [];
    setRecordingTime(0);
    setRecordingMetadata(null);
  };

  // Memoize the video URL to prevent re-creating it on every render
  const videoUrl = useMemo(() => {
    return recordedBlob ? URL.createObjectURL(recordedBlob) : null;
  }, [recordedBlob]);

  // Effect to update video preview when stream changes
  useEffect(() => {
    if (videoPreviewRef.current && previewStream) {
      videoPreviewRef.current.srcObject = previewStream;
      videoPreviewRef.current.play().catch(() => {
        // Silently handle play error
      });
    } else if (videoPreviewRef.current && !previewStream) {
      videoPreviewRef.current.srcObject = null;
    }
  }, [previewStream]);

  const handleUpload = async () => {
    if (!recordedBlob || isUploading) return;
    await uploadRecording(recordedBlob);
  };

  const uploadRecording = async (blob: Blob) => {
    try {
      setIsUploading(true);
      const toastId = showCustomToast("Uploading recording...", "loading");

      if (blob.size < 1000) {
        throw new Error("Recording is too small - may be invalid");
      }

      // Wait for S3 finalization if still processing
      let videoData: string;
      if (s3VideoUrl) {
        // S3 upload already complete, use the URL
        videoData = s3VideoUrl;
      } else if (s3FinalizePromiseRef.current) {
        // S3 upload in progress, wait for it (keep showing "Uploading recording...")
        const result = await s3FinalizePromiseRef.current;
        videoData = result.url;
      } else {
        // Fallback to base64 if progressive upload wasn't used
        videoData = await blobToBase64(blob);
      }

      // Submit the recording link with metadata
      // Use 'videoData' key for both S3 URLs and base64 data

      const response = await fetch(
        `${API_URL}/recording-links/${token}/submit`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            videoData: videoData,
            title: linkData?.title || "Screen Recording",
            metadata: recordingMetadata,
          }),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Submit failed:", response.status, errorText);
        throw new Error(`Failed to submit recording: ${response.status}`);
      }

      await response.json();

      toast.dismiss(toastId);
      setUploadSuccess(true);
      setRecordedBlob(null);
      setS3VideoUrl(null);
    } catch (error: any) {
      toast.dismiss();
      showCustomToast(
        error.message || "Failed to upload recording",
        "exclamation"
      );
    } finally {
      setIsUploading(false);
    }
  };

  const blobToBase64 = (blob: Blob): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (typeof reader.result === "string") {
          resolve(reader.result);
        } else {
          reject(new Error("Failed to convert blob to base64"));
        }
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  if (isValidating) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-16 h-16 rounded-full border-4 border-white/[0.2] border-t-white animate-spin" />
      </div>
    );
  }

  if (!isValid) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-gray-200 rounded-[24px] p-8 w-[480px]">
          <div className="flex items-center gap-3 mb-4">
            <img src={ToolbarLogo} className="w-8 h-8" />
            <h1 className="text-[24px] font-semibold text-white">Toolbar</h1>
          </div>
          <p className="text-[16px] text-white/[0.72]">
            This recording link is invalid or has already been used.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen onest-md bg-gray-50 flex items-center relative justify-center p-4">
      <a href="/">
        <img
          src={ToolbarLogo}
          className="w-9 h-9 absolute top-[16px] left-1/2 translate-x-[-50%]"
        />
      </a>
      <div className="w-full max-w-[480px]">
        {!uploadSuccess && !isRecording && !recordedBlob && (
          <div className="text-center">
            {/* <img src={ToolbarLogo} className="w-8 h-8" /> */}
            <h1 className="text-[28px] font-medium text-white">
              Get ready to record
            </h1>
            <p className="text-white/[0.32] mt-3 text-sm">
              Bug reporting powered by Toolbar
            </p>
            {linkData?.redirectLink && (
              <p className="text-white/[0.32] mt-11 text-sm">
                Once the recording starts you'll be redirected to{" "}
                {linkData?.redirectLink}.<br />
                Come back to this page once you're done recording.
              </p>
            )}
          </div>
        )}
        {isRecording && (
          <div className="text-center">
            <h1 className="text-[28px] font-medium">
              <span className="text-white">Recording your screen </span>
              <span className="text-white/[0.48]">
                {formatTime(recordingTime)}
              </span>
            </h1>
            <p className="text-white/[0.32] mt-3 text-sm">
              Come back to this page once you're done recording.
            </p>
          </div>
        )}
        {recordedBlob && !uploadSuccess && (
          <div className="text-center">
            <h1 className="text-[28px] font-medium text-white">
              The last step, send the video
            </h1>
            <p className="text-white/[0.32] mt-3 text-sm">
              Now you can send the video that was recorded
            </p>
          </div>
        )}

        <div
          className={`flex flex-col items-center justify-center ${
            !isRecording && !recordedBlob && linkData?.redirectLink
              ? "pt-4"
              : "pt-12"
          }`}
        >
          {uploadSuccess ? (
            <div className="flex flex-col items-center">
              {/* <img src={CatLike} className="h-[240px]" /> */}
              <p className="text-[28px] font-medium text-white">
                Done! Thank you!
              </p>
              <p className="text-[14px] mt-[12px] text-white/[0.72] text-center">
                We saved your recording, you can close this tab now.
              </p>
              <a
                href="https://chromewebstore.google.com/detail/toolbar-bug-reports-with/jjikojjfkjcbjklhldnjdnelfmjjmifg"
                target="_blank"
                className="w-[320px] flex items-center justify-center mt-12 h-[44px] cursor-pointer px-8 transition-all text-[14px] leading-[20px] font-medium bg-white/[0.04] hover:bg-white/[0.08] text-white rounded-[16px]"
              >
                Download Toolbar Extension
              </a>
            </div>
          ) : !isRecording && !recordedBlob ? (
            <>
              <div className="flex gap-4 items-center">
                {btns.map((btn, i) => {
                  const isSelected = selectedType === i;
                  return (
                    <div
                      key={i}
                      onClick={async () => {
                        setSelectedType(i);
                        if (btn.withMicrophone) {
                          await getAudioDevices();
                          setShowMicSelection(true);
                        } else {
                          setShowMicSelection(false);
                        }
                      }}
                      className={`onest-md rounded-[24px] w-[232px] h-[124px] flex flex-col items-center justify-center border-[1px] cursor-pointer transition-all ${
                        isSelected
                          ? "border-yellow-600 border-[2px] bg-yellow-600/[0.04]"
                          : "border-white/[0.08] border-[1px] bg-white/[0.04] hover:border-white/[0.16]"
                      }`}
                    >
                      <img
                        src={isSelected ? btn.iconSelected : btn.icon}
                        alt={btn.title}
                      />
                      <p className="mt-[8px] text-[14px] leading-[20px] text-white">
                        {btn.title}
                      </p>
                    </div>
                  );
                })}
              </div>

              <div className="mt-12 w-full max-w-[320px]">
                {showMicSelection && btns[selectedType].withMicrophone && (
                  <div ref={micDropdownRef} className="mb-4 w-full max-w-[320px] relative">
                    <button
                      onClick={() => setIsMicDropdownOpen(!isMicDropdownOpen)}
                      className="w-full h-[44px] px-3 text-[14px] bg-white/[0.04] text-white rounded-[16px] gap-1.5 focus:outline-none cursor-pointer flex items-center justify-between"
                    >
                      <div className="flex gap-1.5 items-center truncate">
                        <img src={Microphone} />
                        <span className="truncate">
                          {audioDevices.find(
                            (d) => d.deviceId === selectedMicId
                          )?.label || `Microphone ${selectedMicId.slice(0, 8)}`}
                        </span>
                      </div>
                      <img
                        src={ChevronDownSmWhite}
                        className={`transition-transform ${
                          isMicDropdownOpen ? "rotate-180" : ""
                        }`}
                      />
                    </button>

                    <AnimatePresence>
                      {isMicDropdownOpen && (
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          className="dropdown-shadow absolute top-[calc(100%+8px)] z-[1000] bg-gray-300 left-0 w-full rounded-[16px]"
                        >
                          <ul className="p-1 flex flex-col gap-1 overflow-scroll max-h-[124px] no-scrollbar">
                            {audioDevices.map((device) => (
                              <li
                                key={device.deviceId}
                                onClick={() => {
                                  setSelectedMicId(device.deviceId);
                                  setIsMicDropdownOpen(false);
                                }}
                                className={`${
                                  selectedMicId === device.deviceId
                                    ? "bg-white/[0.08]"
                                    : ""
                                } truncate transition-all hover:bg-white/[0.16] text-[14px] leading-[20px] gap-2 shrink-0 font-medium h-[36px] px-2 rounded-[12px] flex items-center justify-between cursor-pointer`}
                              >
                                <span className="truncate">
                                  {device.label ||
                                    `Microphone ${device.deviceId.slice(0, 8)}`}
                                </span>
                                {selectedMicId === device.deviceId && (
                                  <img src={Checkmark} alt="Selected" />
                                )}
                              </li>
                            ))}
                          </ul>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                )}

                <button
                  onClick={() =>
                    startRecording(btns[selectedType].withMicrophone)
                  }
                  className="w-full h-[44px] px-6 transition-all text-[14px] leading-[20px] font-medium bg-white hover:bg-white/[0.8] text-gray-50 rounded-[16px] flex items-center justify-center gap-2"
                >
                  <img src={RecordingIcon} />
                  Start Recording
                </button>
              </div>
            </>
          ) : recordedBlob ? (
            <div className="flex flex-col items-center w-full">
              <div className="relative w-full max-w-[480px] bg-gray-300 rounded-[24px] border-[1px] border-white/[0.08] overflow-hidden">
                <video
                  ref={playbackVideoRef}
                  src={videoUrl || ""}
                  className="w-full h-auto"
                  autoPlay
                  loop
                  muted={isMuted}
                  playsInline
                />
                <button
                  onClick={() => setIsMuted(!isMuted)}
                  className="absolute bottom-4 left-4 w-[32px] h-[32px] rounded-[10px] bg-gray-400/[0.4] backdrop-blur-[16px] hover:bg-black/70 transition-all flex items-center justify-center"
                >
                  {isMuted ? <img src={MuteSm} /> : <img src={Volume} />}
                </button>
              </div>
              <div className="flex flex-col gap-4 mt-12">
                <button
                  onClick={handleUpload}
                  disabled={isUploading}
                  className={`w-[320px] flex justify-center h-[44px] px-8 transition-all text-[14px] leading-[20px] font-medium gap-1.5 items-center text-black rounded-[16px] ${
                    isUploading
                      ? "bg-yellow-700/[0.5] cursor-not-allowed"
                      : "bg-yellow-700 hover:bg-yellow-700/[0.8] cursor-pointer"
                  }`}
                >
                  <img src={SendPaper} />
                  {isUploading ? "Submitting..." : "Submit recording"}
                </button>

                <button
                  onClick={handleTryAgain}
                  disabled={isUploading}
                  className={`w-[320px] h-[44px] px-8 transition-all text-[14px] leading-[20px] font-medium rounded-[16px] ${
                    isUploading
                      ? "bg-white/[0.02] text-white/[0.4] cursor-not-allowed"
                      : "bg-white/[0.04] hover:bg-white/[0.08] text-white cursor-pointer"
                  }`}
                >
                  Try again
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center w-full">
              <div className="relative w-full border-[1px] border-white/[0.08] max-w-[480px] bg-gray-300 rounded-[24px] overflow-hidden">
                <video
                  ref={videoPreviewRef}
                  autoPlay
                  muted
                  playsInline
                  className="w-full h-auto"
                />
                <div className="absolute top-4 right-4 flex items-center gap-2 bg-red-700 px-3 py-1.5 rounded-full">
                  <div className="w-2 h-2 rounded-full bg-white animate-pulse" />
                  <span className="text-white text-xs font-medium">REC</span>
                </div>
              </div>
              <button
                onClick={stopRecording}
                className="w-[320px] flex gap-[8.5px] justify-center items-center mt-12 h-[44px] cursor-pointer px-8 transition-all text-[14px] leading-[20px] font-medium bg-red-700 hover:bg-red-700/[0.8] text-white rounded-[16px]"
              >
                <div className="w-[15px] h-[15px] rounded-[5px] bg-white" />
                Stop Recording
              </button>
              <button
                onClick={() => {
                  // Set cancelling flag to prevent saving
                  isCancellingRef.current = true;

                  // Stop recording without saving
                  if (mediaRecorder && mediaRecorder.state !== "inactive") {
                    mediaRecorder.stop();
                    if (mediaRecorder.stream) {
                      mediaRecorder.stream
                        .getTracks()
                        .forEach((track) => track.stop());
                    }
                  }

                  // Clear preview stream
                  setPreviewStream(null);

                  // Stop timer
                  if (timerIntervalRef.current) {
                    clearInterval(timerIntervalRef.current);
                    timerIntervalRef.current = null;
                  }

                  // Reset state
                  chunksRef.current = [];
                  setRecordingTime(0);
                  setRecordedBlob(null);
                  setRecordingMetadata(null);
                }}
                className="w-[320px] mt-4 h-[44px] cursor-pointer px-8 transition-all text-[14px] leading-[20px] font-medium bg-white/[0.04] hover:bg-white/[0.08] text-white rounded-[16px]"
              >
                Cancel and start over
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RecordLink;
